/* eslint-disable react-hooks/exhaustive-deps */
import { Icon, Icons } from 'components/Icon';
import { InfoModal } from 'components/Modal';
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  useCreateBank,
  useGetNigeriaBanks,
  useVerifyBVN,
  useVerifyBankAccount
} from 'common/queries-and-mutations/bank';
import { debounce } from 'lodash';
import { BankType } from 'apps/user/types/on-boarding.type';
import { toast } from 'react-toastify';
import { Alert } from 'components/Toast';
import Button from 'components/Form/Button';
import TextField from 'components/Form/TextField';
import CustomSelect from 'components/Form/CustomSelect';
import { TypeOfBankAccount } from 'constants/bank-account-type';
import { countriesCurrency } from 'constants/countries-currency';
import { isValidNumber } from 'utils';

type Props = {
  onClose: () => void;
  onSuccess: () => void;
};

export default function AddBankModal({ onClose, onSuccess }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchAccountNumber, setSearchAccountNumber] = useState('');
  const [searchBVN, setSearchBVN] = useState('');

  const { mutate, isSuccess, isError, isLoading } = useCreateBank();
  const {
    mutate: verifyAccount,
    isSuccess: isVerified,
    isLoading: isFetching,
    data: verifyData
  } = useVerifyBankAccount();
  const {
    mutate: verifyBVN,
    isSuccess: isBVNVerified,
    isLoading: isBVNFetching,
    data: bvnData
  } = useVerifyBVN();

  const { data: bankList } = useGetNigeriaBanks();

  const computeBankList = useMemo(() => {
    return bankList?.map(bank => ({
      value: bank.code,
      label: bank.name
    }));
  }, [bankList]);

  const formik = useFormik({
    initialValues: {
      banks: [
        {
          bankId: null,
          accountName: '',
          accountNumber: '',
          bvn: '',
          accountType: '',
          bankName: '',
          bankCode: '',
          bankCountry: '',
          branch: ''
        }
      ]
    },
    validationSchema: Yup.object({
      banks: Yup.array().of(
        Yup.object().shape({
          bankCountry: Yup.string()
            .required('Country of the bank is required')
            .nullable(),
          bankName: Yup.string().required('Please select bank'),
          accountNumber: Yup.string()
            .required('Account number is required')
            .nullable(),
          accountName: Yup.string()
            .required('Account name is required')
            .nullable(),
          accountType: Yup.string().when('bankCountry', {
            is: 'Nigeria',
            then: Yup.string().required('Account Type is required').nullable(),
            otherwise: Yup.string()
          })
          // bvn: Yup.string()
          //   .required('Postcode is required').nullable()
          //   .test(
          //     'is-allowed',
          //     'Please provide a valid bvn',
          //     async function (value) {
          //       if (value) {
          //         const isValid = await services.bankService.verifyBVN(value);
          //         return isValid;
          //       }
          //       return false;
          //     }
          //   )
        })
      )
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onBankSubmit(values);
    }
  });

  const handleSearchAndUpdateForAccount = useCallback(() => {
    const { banks } = formik.values;
    const bankIndex = banks.findIndex(
      bank => bank.accountNumber === searchAccountNumber
    );
    if (bankIndex !== -1) {
      if (verifyData) {
        formik.setFieldValue(
          `banks[${bankIndex}].accountName`,
          verifyData?.account_name.toUpperCase()
        );
      } else {
        formik.setFieldValue(`banks[${bankIndex}].accountName`, '');
      }
    }
  }, [formik, verifyData]);

  useEffect(() => {
    if (isVerified && !isFetching) {
      handleSearchAndUpdateForAccount();
    }
  }, [isFetching, isVerified]);

  const executeSearch = useCallback(
    debounce((bankCode: string, accountNumber: string) => {
      verifyAccount({ bankCode, accountNo: accountNumber });
    }, 2000),
    []
  );

  const executeBVNSearch = useCallback(
    debounce((bvn: string) => {
      verifyBVN(bvn);
    }, 2000),
    []
  );

  const handleChange = (bankCode: string, accountNumber: string) => {
    if (accountNumber) {
      setSearchAccountNumber(accountNumber.toString());
      executeSearch(bankCode, accountNumber);
    }
  };

  const handleBVNChange = (bvn: string) => {
    if (bvn) {
      setSearchBVN(bvn.toString());
      executeBVNSearch(bvn);
    }
  };

  const onBankSelect = (
    index: number,
    option: {
      label: string;
      value: string | number;
    } | null
  ) => {
    formik.setFieldValue(`banks[${index}].bankName`, option?.label);
    formik.setFieldValue(`banks[${index}].bankCode`, option?.value);
    const bank = bankList?.find(bank => bank.code === option?.value);
    formik.setFieldValue(`banks[${index}].bankCountry`, bank?.country);
    formik.setFieldValue(`banks[${index}].accountNumber`, '');
    formik.setFieldValue(`banks[${index}].accountName`, '');
  };

  const handleSearchAndUpdateForBVN = useCallback(() => {
    const { banks } = formik.values;
    const bankIndex = banks.findIndex(bank => bank.bvn === searchBVN);
    if (bankIndex !== -1) {
      if (bvnData) {
        formik.setFieldError(`banks[${bankIndex}].bvn`, '');
      } else {
        formik.setFieldError(`banks[${bankIndex}].bvn`, 'Provide valid bvn');
      }
    }
  }, [formik, bvnData]);

  useEffect(() => {
    if (isBVNVerified && !isBVNFetching) {
      handleSearchAndUpdateForBVN();
    }
  }, [isFetching, isVerified]);

  const onBankSubmit = (formValue: BankType) => {
    mutate(formValue);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      toast(<Alert message="Bank Successfully Added" type="success" />);
      onSuccess();
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError]);

  const handleAccountNumberChange = (
    index: number,
    bankCountry: string,
    bankCode: any,
    e: ChangeEvent<any>
  ) => {
    const accountNumber = e.target.value;

    if (isValidNumber(accountNumber) || accountNumber === '') {
      if (bankCountry === 'Nigeria' && accountNumber.length <= 10) {
        formik.handleChange(e);
        handleChange(bankCode, accountNumber);
      } else if (bankCountry === 'Nigeria' && accountNumber.length >= 11) {
        formik.setFieldError(
          `banks[${index}].accountNumber`,
          'Invalid account number provided'
        );
      } else if (bankCountry !== 'Nigeria') {
        formik.handleChange(e);
      }
    }
  };

  return (
    <div>
      <InfoModal width="w-[60%]" className="h-fit rounded-md">
        <div className="flex flex-row items-center justify-between border-b p-4">
          <p className="text-2xl font-bold text-[#353535]">
            Add Bank Information
          </p>
          <button type="button" onClick={onClose}>
            <Icon name={Icons.Close} />
          </button>
        </div>

        <form
          autoComplete="false"
          className="p-8"
          onSubmit={formik.handleSubmit}
        >
          {formik.values.banks.map((bank, index) => (
            <div key={index} className="my-8">
              <div className="grid md:grid-cols-2 md:gap-6">
                <div className="group relative z-0 w-full">
                  <CustomSelect
                    options={countriesCurrency.map(country => ({
                      value: country.name,
                      label: country.name
                    }))}
                    selectedOption={{
                      value: bank.bankCountry,
                      label: bank.bankCountry
                    }}
                    handleOptionChange={option => {
                      formik.resetForm();
                      formik.setFieldValue(
                        `banks[${index}].bankCountry`,
                        option?.value
                      );
                    }}
                    label="Country of the Bank Account"
                    name="bankCountry"
                    isRequired
                    isSearchable
                    className="h-[3rem] rounded-2xl"
                    error={
                      formik.touched.banks
                        ? formik.errors.banks?.[index] &&
                          formik.errors.banks.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.banks[index])
                            ).bankCountry
                          : ''
                        : ''
                    }
                  />
                </div>
                {bank.bankCountry === 'Nigeria' ? (
                  <div className="group relative z-0 mb-6 w-full">
                    <CustomSelect
                      options={computeBankList}
                      selectedOption={{
                        value: bank.bankCode,
                        label: bank.bankName
                      }}
                      handleOptionChange={option => onBankSelect(index, option)}
                      label="Select Bank"
                      name="bank"
                      isRequired
                      isSearchable
                      className="h-[3rem] rounded-2xl"
                      error={
                        formik.touched.banks
                          ? formik.errors.banks?.[index] &&
                            formik.errors.banks.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.banks[index])
                              ).bankName
                            : ''
                          : ''
                      }
                    />
                  </div>
                ) : (
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`banks[${index}].bankName`}
                      placeholder=""
                      value={bank.bankName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Bank Name"
                      error={
                        formik.touched.banks
                          ? formik.errors.banks?.[index] &&
                            formik.errors.banks.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.banks[index])
                              ).bankName
                            : ''
                          : ''
                      }
                      isRequired
                      size="lg"
                      className="h-[3rem] rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                )}
              </div>
              <div className="grid md:grid-cols-2 md:gap-6">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`banks[${index}].accountNumber`}
                    placeholder=""
                    value={bank.accountNumber}
                    onChange={e =>
                      handleAccountNumberChange(
                        index,
                        bank.bankCountry,
                        bank.bankCode,
                        e
                      )
                    }
                    onBlur={e =>
                      handleAccountNumberChange(
                        index,
                        bank.bankCountry,
                        bank.bankCode,
                        e
                      )
                    }
                    label="Account Number"
                    error={
                      formik.touched.banks
                        ? formik.errors.banks?.[index] &&
                          formik.errors.banks.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.banks[index])
                            ).accountNumber
                          : ''
                        : ''
                    }
                    isRequired
                    size="lg"
                    className="h-[3rem] rounded-2xl border-[1.5px] border-gray-300"
                    isLoading={isFetching}
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`banks[${index}].accountName`}
                    placeholder=""
                    value={bank.accountName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Account Name"
                    disabled={bank.bankCountry === 'Nigeria'}
                    error={
                      formik.touched.banks
                        ? formik.errors.banks?.[index] &&
                          formik.errors.banks.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.banks[index])
                            ).accountName
                          : ''
                        : ''
                    }
                    isRequired
                    size="lg"
                    className="h-[3rem] rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
              </div>
              {bank.bankCountry === 'Nigeria' && (
                <>
                  <div className="grid md:grid-cols-2 md:gap-6">
                    <div className="group relative z-0 mb-6 w-full">
                      <TextField
                        name={`banks[${index}].bvn`}
                        placeholder=""
                        value={bank.bvn}
                        onChange={e => {
                          formik.handleChange(e);
                          if (e.target.value.length === 11) {
                            handleBVNChange(e.target.value);
                          }
                        }}
                        onBlur={formik.handleBlur}
                        label="BVN"
                        error={
                          formik.touched.banks
                            ? formik.errors.banks?.[index] &&
                              formik.errors.banks.length > 0
                              ? JSON.parse(
                                  JSON.stringify(formik.errors.banks[index])
                                ).bvn
                              : ''
                            : ''
                        }
                        size="lg"
                        className="h-[3rem] rounded-2xl border-[1.5px] border-gray-300"
                        isLoading={isBVNFetching}
                        isRequired
                      />
                    </div>
                    <div className="group relative z-0 mb-6 w-full">
                      <CustomSelect
                        options={TypeOfBankAccount}
                        selectedOption={{
                          value: bank.accountType,
                          label: bank.accountType
                        }}
                        handleOptionChange={option => {
                          formik.setFieldValue(
                            `banks[${index}].accountType`,
                            option?.value
                          );
                        }}
                        label="Account Type"
                        name="bank"
                        isRequired
                        isSearchable
                        className="h-[3rem] rounded-2xl"
                        error={
                          formik.touched.banks
                            ? formik.errors.banks?.[index] &&
                              formik.errors.banks.length > 0
                              ? JSON.parse(
                                  JSON.stringify(formik.errors.banks[index])
                                ).accountType
                              : ''
                            : ''
                        }
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 md:gap-6">
                    <div className="group relative z-0 mb-6 w-full">
                      <TextField
                        name={`banks[${index}].branch`}
                        placeholder=""
                        value={bank.branch}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled
                        label="Bank Branch"
                        error={
                          formik.touched.banks
                            ? formik.errors.banks?.[index] &&
                              formik.errors.banks.length > 0
                              ? JSON.parse(
                                  JSON.stringify(formik.errors.banks[index])
                                ).branch
                              : ''
                            : ''
                        }
                        size="lg"
                        className="h-[3rem] rounded-2xl border-[1.5px] border-gray-300"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}

          <div className="mt-5 flex items-center justify-between gap-2">
            <div className="flex w-full items-center gap-2">
              <Icon name={Icons.Required} />
              <span className="text-[10px] text-[#7A7C87]">
                Indicates Required
              </span>
            </div>
          </div>

          <div className="mt-10 flex items-end justify-end gap-8">
            <Button
              variant="primary"
              size="custom"
              className="mt-5 rounded p-3"
              label="Add New Account"
              type="submit"
              disabled={!formik.isValid}
              loading={isSubmitting || isLoading}
            ></Button>
          </div>
        </form>
      </InfoModal>
    </div>
  );
}
